import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography, useTheme } from '@mui/material';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import { Plan } from 'src/dto/Credits';
import { useGetPlansQuery, useUpdatePlansMutation } from 'src/redux/services/plans';
import { CustomInputAdornment } from 'src/style/common';
import CustomBillingConfiguration, { Field } from './CustomBillingConfiguration';
import Row from 'src/frontend-common/components/Row';
import ButtonComponent from 'src/components/ButtonComponent';
import EmptyState from 'src/frontend-common/components/EmptyState';
import CompanyInfoSkeleton from 'src/pages/Settings/components/CompanyInfo/CompanyInfoSkeleton';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';

export interface Props {
  selectedAppKey: string;
}

const CustomBilling: React.FC<Props> = ({ selectedAppKey }) => {
  const [dataFilteredByAppKey, setDataFilteredByAppKey] = useState<Plan | undefined>(undefined);
  const [phonePrice, setPhonePrice] = useState<string>('');
  const [emailPrice, setEmailPrice] = useState<string>('');
  const [ipPrice, setIpPrice] = useState<string>('');
  const [devicePrice, setDevicePrice] = useState<string>('');
  const [browserPrice, setBrowserPrice] = useState<string>('');
  const [namePrice, setNamePrice] = useState<string>('');
  const [loginPrice, setLoginPrice] = useState<string>('');
  const [botDetectionPrice, setBotDetectionPrice] = useState<string>('');
  const [domainPrice, setDomainPrice] = useState<string>('');
  const [vatCodePrice, setVatCodePrice] = useState<string>('');
  const [taxIdPrice, setTaxIdPrice] = useState<string>('');
  const [scoreRatePrice, setScoreRatePrice] = useState<string>('');
  const [resetData, setResetData] = useState<boolean>(false);

  const fields: Field<string>[] = [
    {
      label: 'Phone Price',
      value: phonePrice,
      setValue: setPhonePrice,
    },
    { label: 'Email Price', value: emailPrice, setValue: setEmailPrice },
    { label: 'IP Price', value: ipPrice, setValue: setIpPrice },
    {
      label: 'Device Price',
      value: devicePrice,
      setValue: setDevicePrice,
    },
    {
      label: 'Browser Price',
      value: browserPrice,
      setValue: setBrowserPrice,
    },
    { label: 'Name Price', value: namePrice, setValue: setNamePrice },
    {
      label: 'VAT Code',
      value: vatCodePrice,
      setValue: setVatCodePrice,
    },
    { label: 'TAX ID', value: taxIdPrice, setValue: setTaxIdPrice },

    { label: 'Domain', value: domainPrice, setValue: setDomainPrice },
  ];

  const { data: dataPlans, isLoading: isLoadingPlans, isError: isErrorPlans } = useGetPlansQuery();

  const [updatePlans, { isSuccess: updatePlansIsSuccess, isError: updatePlansIsError, error: updatePlansError }] = useUpdatePlansMutation();

  useEffect(() => {
    if (dataPlans) {
      const currentPlan = dataPlans.items.find((item) => item.app_key === selectedAppKey);
      setDataFilteredByAppKey(currentPlan);
      setPhonePrice(String(currentPlan?.phone_rate) || '');
      setEmailPrice(String(currentPlan?.email_rate) || '');
      setIpPrice(String(currentPlan?.ip_rate) || '');
      setDevicePrice(String(currentPlan?.device_rate) || '');
      setBrowserPrice(String(currentPlan?.browser_rate) || '');
      setNamePrice(String(currentPlan?.name_rate) || '');
      setVatCodePrice(String(currentPlan?.vat_code_rate) || '');
      setTaxIdPrice(String(currentPlan?.tax_id_rate) || '');
      setDomainPrice(String(currentPlan?.domain_rate) || '');
      setLoginPrice(String(currentPlan?.login_rate) || '');
      setBotDetectionPrice(String(currentPlan?.bot_detection_rate) || '');
      setScoreRatePrice(String(currentPlan?.score_rate) || '');
    }
  }, [dataPlans, selectedAppKey, resetData]);

  const theme = useTheme();

  const handleUpdate = () => {
    updatePlans({
      app_key: selectedAppKey,
      name: selectedAppKey,
      phone_rate: Number(phonePrice),
      email_rate: Number(emailPrice),
      ip_rate: Number(ipPrice),
      device_rate: Number(devicePrice),
      browser_rate: Number(browserPrice),
      name_rate: Number(namePrice),
      vat_code_rate: Number(vatCodePrice),
      tax_id_rate: Number(taxIdPrice),
      domain_rate: Number(domainPrice),
      login_rate: Number(loginPrice),
      bot_detection_rate: Number(botDetectionPrice),
      score_rate: Number(scoreRatePrice),
    });
  };

  return (
    <>
      {isLoadingPlans && <CompanyInfoSkeleton />}
      {!isLoadingPlans && isErrorPlans && <FailureScreen testId="dataplans-fail">An error occurred while fetching billing data, please retry</FailureScreen>}
      {!isLoadingPlans && !isErrorPlans && dataFilteredByAppKey && (
        <>
          <Typography variant="h5" style={{ marginBottom: 32 }}>
            Products
          </Typography>
          <Grid container={true} spacing={3}>
            {fields.map((field) => (
              <Grid item={true} xs={12} md={6} key={field.label}>
                <TextField
                  variant="outlined"
                  fullWidth={true}
                  value={field.value}
                  error={field.value.trim() === '' || Number(field.value) < 0}
                  label={field.label}
                  type="number"
                  onChange={(result) => field.setValue(result.target.value)}
                  InputProps={{
                    startAdornment: <CustomInputAdornment position="start">{useGetCurrency()}</CustomInputAdornment>,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <CustomBillingConfiguration
            appKey={selectedAppKey}
            scoreRate={scoreRatePrice}
            setScoreRate={setScoreRatePrice}
            loginRate={loginPrice}
            setLoginRate={setLoginPrice}
            botDetectionRate={botDetectionPrice}
            setBotDetectionRate={setBotDetectionPrice}
          />
          <Grid item={true} xs={12}>
            <Row hAlign="flex-end">
              <ButtonComponent buttontype="cancel" onClick={() => setResetData((prev) => !prev)} sx={{ marginRight: theme.spacing(3) }} />

              <ButtonComponent buttontype="mainaction" text="Update" onClick={handleUpdate} />
            </Row>
          </Grid>
        </>
      )}
      {!isLoadingPlans && !isErrorPlans && !dataFilteredByAppKey && <EmptyState />}
      <SnackBarCompleteHandler
        isSuccess={updatePlansIsSuccess}
        isError={updatePlansIsError}
        error={updatePlansError}
        successMessage="Plan successfully updated"
      />
    </>
  );
};

export default CustomBilling;
